import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ReactComponent as FPSIcon } from '@/assets/icons/fps.svg';
import { LeftRightPositioning, MoneyAmountItem, ReadonlyItem, Space } from '@/components';
import { makeSelectIsMobileView } from '@/model/global/selectors';
import { BankReadonlyValue, ReceiverItem, SenderItem, WalletItem } from '@/pages/withdrawal/components';
import { formatDateTime } from '@/utils/date';

import { FieldsProps } from './types';

const selectIsMobile = makeSelectIsMobileView();

const ResultFields: React.FC<FieldsProps> = ({ isCredit = true, data, className, 'data-test': dataTest }) => {
  const { formatMessage } = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const amount = Math.abs(data?.amount || 0);
  return (
    <Space direction="vertical" size={2} className={className}>
      {data?.isFPS && (
        <ReadonlyItem
          label={<FormattedMessage id="withdrawal.fields.operation.label" />}
          data-test={dataTest && `${dataTest}-operation`}
        >
          <LeftRightPositioning
            left={<FormattedMessage id="withdrawal.fields.operation.text" />}
            right={!isMobile && <FPSIcon />}
          />
        </ReadonlyItem>
      )}
      <ReadonlyItem
        data-test={dataTest && `${dataTest}-date`}
        label={<FormattedMessage id="withdrawal.fields.date.label" />}
      >
        {formatDateTime(data?.createdAt)}
      </ReadonlyItem>
      <MoneyAmountItem
        data-test={dataTest && `${dataTest}-amount`}
        value={{ value: amount, hasError: false }}
        readonly
        messages={{ label: formatMessage({ id: 'withdrawal.fields.amount.label' }) }}
      />
      <MoneyAmountItem
        data-test={dataTest && `${dataTest}-fee`}
        value={{ value: data?.fee && Math.abs(data?.fee), hasError: false }}
        readonly
        messages={{ label: formatMessage({ id: 'withdrawal.fields.fee.label' }) }}
      />
      <WalletItem
        data-test={dataTest && `${dataTest}-wallet`}
        isCredit={isCredit}
        value={{ value: data?.wallet, hasError: false }}
        readonly
      />
      {data?.person?.phone ? (
        <>
          {isCredit ? (
            <ReceiverItem
              data-test={dataTest && `${dataTest}-receiver`}
              shortPhoneMask
              value={{
                value: {
                  phone: data.person.phone,
                  name: data.person.name,
                },
                hasError: false,
              }}
              readonly
            />
          ) : (
            <SenderItem
              data-test={dataTest && `${dataTest}-sender`}
              value={{
                value: {
                  phone: data.person.phone,
                  name: data.person.name,
                },
                hasError: false,
              }}
              readonly
            />
          )}
        </>
      ) : (
        data?.person?.name && (
          <ReadonlyItem
            data-test={dataTest && `${dataTest}-person`}
            label={
              isCredit ? (
                <FormattedMessage id="withdrawal.fields.receiver.label" />
              ) : (
                <FormattedMessage id="withdrawal.fields.sender.label" />
              )
            }
          >
            <Space direction="vertical" size={0}>
              <span className="ym-hide-content" data-test={dataTest && `${dataTest}-person-name`}>
                {data.person.name}
              </span>
            </Space>
          </ReadonlyItem>
        )
      )}
      {data?.bank && (
        <BankReadonlyValue
          data-test={dataTest && `${dataTest}-bank`}
          isCredit={isCredit}
          value={data.bank.id}
          label={data.bank.label}
        />
      )}
      {data?.message && (
        <ReadonlyItem
          data-test={dataTest && `${dataTest}-message`}
          label={<FormattedMessage id="withdrawal.fields.message.label" />}
          textClassName="ym-hide-content"
        >
          {data?.message}
        </ReadonlyItem>
      )}
      {data?.sbpId && (
        <ReadonlyItem
          data-test={dataTest && `${dataTest}-code`}
          label={<FormattedMessage id="withdrawal.fields.code.label" />}
          textClassName="ym-hide-content"
        >
          {data?.sbpId}
        </ReadonlyItem>
      )}
      {data?.id && (
        <ReadonlyItem
          data-test={dataTest && `${dataTest}-id`}
          label={<FormattedMessage id="withdrawal.fields.id.label" />}
          textClassName="ym-hide-content"
        >
          {data?.id}
        </ReadonlyItem>
      )}
    </Space>
  );
};

export default ResultFields;
